export type Locale = "zh" | "en";

export type DemoChoiceId = "inspect" | "question" | "protect";
export type HowJourneyPanel = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export interface HowJourneyStep {
  index: string;
  panel: HowJourneyPanel;
  title: string;
  body: string;
  artAlt: string;
}

export interface DemoBranch {
  id: DemoChoiceId;
  label: string;
  playerLine: string;
  responder: string;
  response: string;
  cue: string;
}

interface SiteCopy {
  meta: {
    lang: string;
    title: string;
    description: string;
    ogLocale: string;
    ogTitle: string;
    ogDescription: string;
    ogImageAlt: string;
    canonical: string;
  };
  brandHomeAria: string;
  languageAria: string;
  skip: string;
  nav: { how: string; story: string; why: string; join: string };
  hero: {
    eyebrow: string;
    title: readonly string[];
    lead: string;
    join: string;
    demo: string;
    status: string;
    scroll: string;
    stageAria: string;
    imageAlt: string;
  };
  phone: {
    aria: string;
    title: string;
    subtitle: string;
    role: string;
    eventLabel: string;
    event: string;
    voice: string;
    quote: string;
    cue: string;
    composer: string;
  };
  worldStrip: { aria: string; intro: string; categories: readonly string[] };
  how: {
    aria: string;
    eyebrow: string;
    title: readonly string[];
    body: string;
    steps: readonly HowJourneyStep[];
  };
  story: {
    eyebrow: string;
    title: readonly string[];
    body: string;
    cardAlt: string;
    cardLabel: string;
    cardTitle: string;
    cardSummary: string;
    characters: readonly string[];
    demoLabel: string;
    demoNote: string;
    caller: string;
    callerLine: string;
    responder: string;
    responderLine: string;
    playerChoice: string;
    prompt: string;
    retry: string;
    branches: readonly DemoBranch[];
  };
  why: {
    eyebrow: string;
    title: readonly string[];
    principles: readonly { index: string; title: string; body: string }[];
  };
  worlds: {
    imageAlt: string;
    eyebrow: string;
    title: readonly string[];
    body: string;
  };
  waitlist: {
    imageAlt: string;
    eyebrow: string;
    title: readonly string[];
    body: string;
    form: {
      company: string;
      label: string;
      submit: string;
      submitting: string;
      emailRequired: string;
      emailInvalid: string;
      invalidSubmit: string;
      consentRequired: string;
      requestError: string;
      consent: string;
      privacy: string;
      note: string;
      successTitle: string;
      successBody: string;
    };
  };
  faq: {
    eyebrow: string;
    title: readonly string[];
    items: readonly { question: string; answer: string }[];
  };
  footer: {
    statement: string;
    waitlist: string;
    privacy: string;
    contact: string;
    operator: string;
  };
}

export const siteCopy = {
  zh: {
    meta: {
      lang: "zh-CN",
      title: "同幕 ACTALE｜进入一个正在发生的故事",
      description: "同幕是一款互动叙事 APP。你扮演故事里的一个角色，其他人物由 AI 演绎，并会记住、回应和追问你的选择。",
      ogLocale: "zh_CN",
      ogTitle: "同幕 ACTALE｜你不是观众，你就是其中的人",
      ogDescription: "选择一个角色，用自己的话进入故事。其他人物会听见、记住并回应你的选择。",
      ogImageAlt: "站在多个故事世界入口的同幕玩家",
      canonical: "https://actale.cc/",
    },
    brandHomeAria: "同幕 ACTALE 首页",
    languageAria: "切换官网语言",
    skip: "跳到主要内容",
    nav: { how: "怎么玩", story: "体验片段", why: "为什么不同", join: "加入内测" },
    hero: {
      eyebrow: "ACTALE · 互动叙事 APP",
      title: ["进入一个", "正在发生的故事。"],
      lead: "你扮演故事里的一个角色，其他人物由 AI 演绎，并会记住、回应和追问你的选择。",
      join: "申请加入内测",
      demo: "看看故事如何回应你",
      status: "Alpha 核心体验打磨中",
      scroll: "向下",
      stageAria: "《回声未接》剧情现场与同幕 APP 真实界面示意",
      imageAlt: "雨夜旧电台里的黑色电话、录音机和07号录音带",
    },
    phone: {
      aria: "故事进行中的真实产品界面",
      title: "回声未接",
      subtitle: "断线来电",
      role: "今晚，你是 沈砚",
      eventLabel: "故事继续发生",
      event: "门外传来第三次敲击声。",
      voice: "失真来电",
      quote: "“你们还有十五分钟。把江葵留下的07号录音带放回播音室。”",
      cue: "录音机忽然开始倒转。",
      composer: "说出你的回答或行动…",
    },
    worldStrip: {
      aria: "同幕未来可承载的故事类型",
      intro: "一个入口，许多可以亲自进入的世界。",
      categories: ["悬疑", "奇幻", "情感", "冒险"],
    },
    how: {
      aria: "四步了解同幕怎么玩",
      eyebrow: "HOW IT WORKS",
      title: ["四步，走进一个", "正在发生的故事。"],
      body: "不用背台词，也不用猜系统想听什么。看懂现场，然后像这个角色一样作出自己的回应。",
      steps: [
        { index: "01", panel: "top-left", title: "选择角色", body: "你不是挑选头像，而是进入一个人的处境。", artAlt: "一只手在三张人物肖像中选择沈砚" },
        { index: "02", panel: "top-right", title: "进入现场", body: "故事已经开始，他们正在等你的回答。", artAlt: "沈砚走进旧电台，林乔和顾寻同时转身看向他" },
        { index: "03", panel: "bottom-left", title: "自由回应", body: "不用背台词，直接说出你想说、想做的事。", artAlt: "沈砚通过手机说话，身边出现简单的声音波形" },
        { index: "04", panel: "bottom-right", title: "故事回应", body: "人物和事件会根据你的选择，继续发生变化。", artAlt: "林乔和顾寻作出回应，电话响起，录音机开始转动" },
      ],
    },
    story: {
      eyebrow: "FEATURED STORY",
      title: ["如果那通电话", "知道你的秘密呢？"],
      body: "点击一个回答，体验角色如何根据你的选择继续现场。这个片段是可控演示，不会消耗模型，也不会泄露正式故事反转。",
      cardAlt: "雨夜旧电台的录音设备与黑色电话",
      cardLabel: "当前主推故事 · 约 15 分钟",
      cardTitle: "回声未接",
      cardSummary: "电台拆除前夜，一通酷似失踪主持人的来电响起。在场三个人中，有人一直在撒谎。",
      characters: ["沈砚", "林乔", "顾寻"],
      demoLabel: "产品体验片段",
      demoNote: "预设演示，不连接真实模型",
      caller: "失真来电",
      callerLine: "“沈砚、林乔、顾寻……你们还有十五分钟。把江葵留下的07号录音带放回播音室。”",
      responder: "顾寻",
      responderLine: "江葵留下的07号录音带，七年前就该被销毁了。沈砚，你怎么看？",
      playerChoice: "沈砚 · 你的选择",
      prompt: "如果今晚你是沈砚，你会——",
      retry: "换一种回答",
      branches: [
        { id: "inspect", label: "先检查07号录音带", playerLine: "先别播放。我想检查录音带和来电线路。", responder: "林乔", response: "林乔翻过磁带盒。内盖画着一段手写波形，下面只有一句话：不要相信完整的声音。", cue: "你的行动会带出新线索，也会改变其他人物对你的判断。" },
        { id: "question", label: "追问顾寻为何召集所有人", playerLine: "顾寻，你为什么偏偏把我们三个人叫回来？", responder: "顾寻", response: "因为你们是最后见过我姐的人。今晚断电前，我要知道七年前是谁让她从电台消失。", cue: "人物会回应你的问题，也会把现场继续推向前。" },
        { id: "protect", label: "暂时隐瞒自己听出的声音", playerLine: "只是线路串音。先把门锁上，谁也别离开。", responder: "林乔", response: "你撒谎的时候，总会先摸一下左耳。七年前是这样，今晚也是。", cue: "其他人物记得你是谁，也会观察并追问你的选择。" },
      ],
    },
    why: {
      eyebrow: "NOT JUST A CHATBOT",
      title: ["重要的不是 AI 会说话。", "而是故事真的在发生。"],
      principles: [
        { index: "01 / 角色", title: "每个人都不是万能的", body: "角色只知道自己应该知道的事。秘密、误解和悄悄话让人物关系真正成立。" },
        { index: "02 / 导演", title: "现场不会突然停住", body: "看不见的幕后导演制造事件、控制节奏，却不会代替玩家说话或作出选择。" },
        { index: "03 / 声音", title: "你听见的是一个人", body: "不同角色拥有不同音色、语速和情绪；语音不是装饰，而是故事表演的一部分。" },
      ],
    },
    worlds: {
      imageAlt: "一个人站在雨夜电台、神秘森林、城市房间和海岸冒险交汇的故事入口",
      eyebrow: "MANY WORLDS, ONE YOU",
      title: ["今天走进谜团。", "明天，也许走进另一个人生。"],
      body: "同幕不是某一种题材的容器。每一条故事线都可以拥有自己的世界、人物、声音和规则；唯一不变的是，你永远身在其中。",
    },
    waitlist: {
      imageAlt: "被海雾包围的孤独灯塔",
      eyebrow: "PRIVATE ALPHA",
      title: ["下一次故事开始，", "希望你在场。"],
      body: "同幕仍在打磨第一条完整的 15 分钟体验。现在留下邮箱，等核心体验达到测试标准后，我们再邀请你进入。",
      form: {
        company: "公司",
        label: "接收内测邀请的邮箱",
        submit: "申请加入内测",
        submitting: "正在提交…",
        emailRequired: "请输入接收邀请的邮箱。",
        emailInvalid: "请输入有效的邮箱地址。",
        invalidSubmit: "请检查邮箱地址后再提交。",
        consentRequired: "请先确认你同意接收内测相关通知。",
        requestError: "暂时没有提交成功，请稍后再试。你的邮箱尚未被保存。",
        consent: "我同意同幕使用此邮箱发送内测邀请和必要的产品测试通知。",
        privacy: "查看隐私说明",
        note: "不会发送营销轰炸；正式开放前也不会出现下载或付费引导。",
        successTitle: "已经为你留下位置",
        successBody: "内测准备好后，我们会通过邮箱通知你。",
      },
    },
    faq: {
      eyebrow: "FAQ",
      title: ["进入之前，", "你可能想知道。"],
      items: [
        { question: "同幕是传统剧本杀吗？", answer: "不是。你不需要提前阅读完整剧本，也没有固定台词。你只掌握所选角色应该知道的内容，并在实时发生的故事中自由回应。" },
        { question: "现在可以下载吗？", answer: "暂时不开放下载。项目仍处于核心体验 Alpha 阶段，我们会先完成故事节奏、角色声音和秘密权限等内部测试，再邀请外部体验者。" },
        { question: "故事会替我自动做决定吗？", answer: "不会。幕后导演可以制造事件和让其他人物主动回应，但玩家角色的发言、行动和关键选择只能由玩家本人决定。" },
        { question: "以后只有悬疑故事吗？", answer: "不会。《回声未接》是当前体验样板。长期会逐步探索奇幻、情感、冒险等适合互动演绎的故事世界。" },
      ],
    },
    footer: { statement: "让每一个回答，都成为故事的一部分。", waitlist: "内测申请", privacy: "隐私说明", contact: "联系我们", operator: "运营：同幕 ACTALE 项目组" },
  },
  en: {
    meta: {
      lang: "en",
      title: "ACTALE | Step into a story already in motion",
      description: "ACTALE is an interactive storytelling app. Play one character while AI performs the rest—remembering, responding to, and questioning your choices.",
      ogLocale: "en_US",
      ogTitle: "ACTALE | You are not the audience. You are in the story.",
      ogDescription: "Choose a role and enter the story in your own words. Every character can hear, remember, and respond to your choices.",
      ogImageAlt: "An ACTALE player standing at the threshold of many story worlds",
      canonical: "https://actale.cc/?lang=en",
    },
    brandHomeAria: "ACTALE home",
    languageAria: "Switch website language",
    skip: "Skip to main content",
    nav: { how: "How it works", story: "Story demo", why: "Why it’s different", join: "Join the alpha" },
    hero: {
      eyebrow: "ACTALE · INTERACTIVE STORY APP",
      title: ["Step into a story", "already in motion."],
      lead: "You play one character. AI performs the rest—remembering, responding to, and questioning your choices.",
      join: "Apply for the alpha",
      demo: "See how the story responds",
      status: "Alpha experience in development",
      scroll: "Explore",
      stageAria: "A scene from Echo Unanswered beside the ACTALE app experience",
      imageAlt: "A black telephone, reel-to-reel recorder, and Archive Tape 07 in an old radio studio on a rainy night",
    },
    phone: {
      aria: "A live story scene inside the ACTALE app",
      title: "Echo Unanswered",
      subtitle: "Disconnected Call",
      role: "Tonight, you are Shen Yan",
      eventLabel: "The story moves",
      event: "A third knock sounds outside the door.",
      voice: "Distorted call",
      quote: "“You have fifteen minutes. Put Jiang Kui’s Archive Tape 07 back in the studio.”",
      cue: "The recorder begins rewinding by itself.",
      composer: "Say what you do or say…",
    },
    worldStrip: {
      aria: "Story genres ACTALE can grow into",
      intro: "One doorway. Many worlds you can enter for yourself.",
      categories: ["Mystery", "Fantasy", "Drama", "Adventure"],
    },
    how: {
      aria: "Learn how ACTALE works in four steps",
      eyebrow: "HOW IT WORKS",
      title: ["Four steps into", "a story already unfolding."],
      body: "No scripts to memorize and no hidden command to guess. Read the scene, then answer as this character—in your own way.",
      steps: [
        { index: "01", panel: "top-left", title: "Choose a role", body: "You are not choosing an avatar. You are stepping into someone’s circumstances.", artAlt: "A hand choosing Shen Yan from three character portraits" },
        { index: "02", panel: "top-right", title: "Enter the scene", body: "The story is already moving. Everyone is waiting for your answer.", artAlt: "Shen Yan enters the old studio as Lin Qiao and Gu Xun turn toward him" },
        { index: "03", panel: "bottom-left", title: "Respond freely", body: "No fixed lines. Say what you want to say or do.", artAlt: "Shen Yan speaks through a phone as a simple voice waveform appears" },
        { index: "04", panel: "bottom-right", title: "Watch it answer", body: "Characters and events keep changing in response to your choices.", artAlt: "Lin Qiao and Gu Xun react as the telephone rings and the recorder starts moving" },
      ],
    },
    story: {
      eyebrow: "FEATURED STORY",
      title: ["What if the call", "knew your secret?"],
      body: "Choose a reply and watch the scene continue around your decision. This is a controlled demo: it uses no live model and reveals no final-story twist.",
      cardAlt: "Recording equipment and a black telephone inside an old radio studio on a rainy night",
      cardLabel: "FEATURED NOW · ABOUT 15 MINUTES",
      cardTitle: "Echo Unanswered",
      cardSummary: "On the eve of the station’s demolition, a call arrives in the voice of a missing host. Someone in the room has been lying.",
      characters: ["Shen Yan", "Lin Qiao", "Gu Xun"],
      demoLabel: "Product demo scene",
      demoNote: "Preset demo · no live model connected",
      caller: "Distorted call",
      callerLine: "“Shen Yan, Lin Qiao, Gu Xun… You have fifteen minutes. Put Jiang Kui’s Archive Tape 07 back in the studio.”",
      responder: "Gu Xun",
      responderLine: "Jiang Kui’s Archive Tape 07 should have been destroyed seven years ago. Shen Yan—what do you think?",
      playerChoice: "Shen Yan · Your choice",
      prompt: "If you were Shen Yan tonight, would you—",
      retry: "Try another answer",
      branches: [
        { id: "inspect", label: "Inspect Archive Tape 07", playerLine: "Don’t play it yet. I want to inspect the tape and the incoming line.", responder: "Lin Qiao", response: "Lin Qiao turns over the case. A waveform is drawn inside, with one sentence beneath it: Don’t trust a complete voice.", cue: "Your action reveals a new clue—and changes how the others judge you." },
        { id: "question", label: "Ask why Gu Xun brought everyone back", playerLine: "Gu Xun, why did you bring the three of us back here?", responder: "Gu Xun", response: "Because you were the last people to see my sister. Before the power goes out tonight, I need to know who made her disappear from this station.", cue: "Characters answer your questions and keep the scene moving." },
        { id: "protect", label: "Hide the voice you recognized", playerLine: "It was only crossed wires. Lock the door. No one leaves.", responder: "Lin Qiao", response: "Whenever you lie, you touch your left ear first. You did it seven years ago. You did it again tonight.", cue: "Other characters remember who you are. They notice your choices—and press you on them." },
      ],
    },
    why: {
      eyebrow: "NOT JUST A CHATBOT",
      title: ["It’s not that AI can speak.", "It’s that the story keeps moving."],
      principles: [
        { index: "01 / CHARACTER", title: "No one knows everything", body: "Characters know only what they should. Secrets, misunderstandings, and whispers make relationships feel real." },
        { index: "02 / DIRECTOR", title: "The scene never stalls", body: "An unseen director creates events and controls the pace without speaking or deciding on the player’s behalf." },
        { index: "03 / VOICE", title: "You hear a person", body: "Every character has a distinct voice, rhythm, and emotion. Audio is part of the performance, not decoration." },
      ],
    },
    worlds: {
      imageAlt: "A person standing where a rainy radio studio, mysterious forest, city room, and coastal adventure converge",
      eyebrow: "MANY WORLDS, ONE YOU",
      title: ["Enter a mystery today.", "Tomorrow, perhaps another life."],
      body: "ACTALE is not a container for one genre. Every story line can have its own world, people, voices, and rules. The one constant is that you are always inside it.",
    },
    waitlist: {
      imageAlt: "A solitary lighthouse surrounded by sea fog",
      eyebrow: "PRIVATE ALPHA",
      title: ["Be there when", "the next story begins."],
      body: "We are still shaping our first complete 15-minute experience. Leave your email now, and we will invite you when the core experience is ready to test.",
      form: {
        company: "Company",
        label: "Email for a private alpha invitation",
        submit: "Apply for the alpha",
        submitting: "Submitting…",
        emailRequired: "Enter the email where you want to receive an invitation.",
        emailInvalid: "Enter a valid email address.",
        invalidSubmit: "Check your email address before submitting.",
        consentRequired: "Confirm that you agree to receive alpha-related updates.",
        requestError: "Your request could not be submitted. Please try again later; your email has not been saved.",
        consent: "I agree that ACTALE may use this email for alpha invitations and essential product-testing updates.",
        privacy: "Read the privacy note",
        note: "No marketing blasts. No download or payment prompts before the product is ready.",
        successTitle: "Your place is saved",
        successBody: "We will email you when the private alpha is ready.",
      },
    },
    faq: {
      eyebrow: "FAQ",
      title: ["Before you enter,", "you may want to know."],
      items: [
        { question: "Is ACTALE a traditional murder-mystery game?", answer: "No. There is no full script to read in advance and no fixed dialogue to perform. You know only what your role should know, then respond freely as the story happens around you." },
        { question: "Can I download it now?", answer: "Not yet. ACTALE is still in its core-experience alpha. We are testing story pacing, character voices, and secret permissions internally before inviting external players." },
        { question: "Will the story make decisions for me?", answer: "No. The unseen director can create events and prompt other characters to respond, but your character’s words, actions, and key decisions belong to you alone." },
        { question: "Will every story be a mystery?", answer: "No. Echo Unanswered is our current experience prototype. Over time, ACTALE will explore fantasy, drama, adventure, and other worlds suited to interactive performance." },
      ],
    },
    footer: { statement: "Let every answer become part of the story.", waitlist: "Private alpha", privacy: "Privacy", contact: "Contact", operator: "Operated by the ACTALE project team" },
  },
} as const satisfies Record<Locale, SiteCopy>;
